import { tracking } from '@iwoca/frontend-tracking-library';
import { IconExternalLink } from '@iwoca/icons';
import { Pill, Skeleton } from '@iwoca/orion';

import { Seller, TERM_SHORT_LABELS } from '../Seller.types';

const TERM_ORDER = ['PAY_IN_30D', 'PAY_IN_3M', 'PAY_IN_12M'];

function normaliseWebsiteUrl(website: string) {
  return website
    .replace('http://', '')
    .replace('https://', '')
    .replace('www.', '')
    .replace(/\/$/, '');
}

function toTitleCase(str: string) {
  return str
    .toLowerCase()
    .replace(/(?:^|\s)\S/g, (match) => match.toUpperCase());
}

function trackSellerClick(seller: Seller) {
  tracking.ampli.sellerDirectorySellerClicked({
    seller_name: seller.name,
    seller_state_key: seller.state_key,
    url: seller.website,
  });
}

export function SellerCard({ seller }: { seller: Seller }) {
  const offerings = Array.isArray(seller.offerings) ? seller.offerings : [];
  const orderedOfferings = TERM_ORDER.flatMap((productType) =>
    offerings.filter((o) => o.product_type === productType),
  );

  return (
    <a
      href={seller.website}
      target="_blank"
      rel="noopener noreferrer"
      onClick={() => trackSellerClick(seller)}
      className="flex min-h-[100px] min-w-[200px] flex-col gap-s rounded-s border border-solid border-secondary-90 bg-structure-100 p-l text-primary-10 no-underline hover:border-primary-40 hover:shadow-black-m"
    >
      <div className="flex w-full items-start justify-between gap-xs">
        <span className="text-l font-bold leading-none">{seller.name}</span>
        <IconExternalLink className="shrink-0 fill-primary-40" />
      </div>

      <div className="flex flex-col gap-xs">
        <span className="truncate text-s font-reg text-secondary-40">
          {normaliseWebsiteUrl(seller.website)}
        </span>
        <span className="w-max">
          <Pill
            variant="london"
            size="small"
            className="border border-solid border-secondary-80 !px-xs !py-2xs !text-xs"
          >
            {toTitleCase(seller.industry)}
          </Pill>
        </span>
      </div>

      <div className="mt-auto flex items-center gap-[4px]">
        {orderedOfferings.map((offering, i) => (
          <span
            key={offering.product_type}
            className="flex items-center gap-[4px]"
          >
            <Pill
              variant={
                offering.fee_structure === 'INTEREST_FREE' ? 'green' : 'london'
              }
              size="small"
              className="!px-xs !py-2xs !text-xs"
            >
              {TERM_SHORT_LABELS[offering.product_type] ??
                offering.product_type}
            </Pill>
            {i < orderedOfferings.length - 1 && (
              <span className="text-xs text-secondary-60">|</span>
            )}
          </span>
        ))}
      </div>
    </a>
  );
}

export function LoadingCard() {
  return (
    <div className="flex min-h-[100px] min-w-[200px] flex-col gap-s rounded-s border border-solid border-secondary-90 bg-structure-100 p-l text-primary-10 no-underline hover:border-primary-40 hover:shadow-black-m">
      <div className="flex w-full items-start justify-between gap-xs">
        <span className="text-l font-bold leading-none">
          <Skeleton />
        </span>
        <IconExternalLink className="shrink-0 fill-primary-40" />
      </div>
      <span className="text-s font-reg text-secondary-40">
        <Skeleton />
      </span>
      <span className="w-max rounded-xs border border-solid border-secondary-90 px-xs py-2xs text-s font-med text-secondary-20">
        <Skeleton />
      </span>
    </div>
  );
}

export function LegacySellerCard({ seller }: { seller: Seller }) {
  return (
    <a
      href={seller.website}
      target="_blank"
      rel="noopener noreferrer"
      onClick={() => trackSellerClick(seller)}
      className="relative flex min-h-[100px] min-w-[200px] flex-col gap-s rounded-s border border-solid border-secondary-90 bg-structure-100 p-m text-primary-10 no-underline hover:border-primary-40 hover:shadow-black-m"
    >
      <IconExternalLink className="absolute right-m top-m fill-primary-40" />
      <span className="w-[calc(100%-20px)] text-m font-bold">
        {seller.name}
      </span>
      <span className="text-xs font-reg">
        {normaliseWebsiteUrl(seller.website)}
      </span>
      <span className="w-max rounded-xs border border-solid border-secondary-90 px-xs py-2xs text-xs font-med text-secondary-40">
        {seller.industry}
      </span>
    </a>
  );
}

export function LegacyLoadingCard() {
  return (
    <div className="relative flex min-h-[100px] min-w-[200px] flex-col gap-s rounded-s border border-solid border-secondary-90 bg-structure-100 p-m text-primary-10 no-underline hover:border-primary-40 hover:shadow-black-m">
      <IconExternalLink className="absolute right-m top-m fill-primary-40" />
      <span className="w-[calc(100%-20px)] text-m font-bold">
        <Skeleton />
      </span>
      <span className="text-xs font-reg">
        <Skeleton />
      </span>
      <span className="w-max rounded-xs border border-solid border-secondary-90 px-xs py-2xs text-xs font-med text-secondary-40">
        <Skeleton />
      </span>
    </div>
  );
}
